import { useState, useMemo, useEffect, useCallback, useRef, useDeferredValue } from "react";
import { useRealtimeCases, Case } from "@/lib/useRealtimeCases";
import { groupNotifications } from "@/lib/notifications";
import { useNotifications } from "@/lib/useNotifications";
import { useSelectedCenters } from "@/lib/SelectedCentersContext";
import { useBrand } from "@/lib/BrandContext";
import { useClientTypeFilter } from "@/lib/ClientTypeFilterContext";
import { detectBrandFromCenter, getCentersForBrand, mergeCenterOptions } from "@/lib/brandConfig";
import { CLIENT_TYPE_OPTIONS, normalizeClientType } from "@/lib/clientTypes";
import { orderedStatuses, statusColors } from "@/lib/statusColors";
import { CaseCard } from "@/components/kanban/CaseCard";
import { useReminderCycles } from "@/lib/useReminderCycles";
import { CaseDrawer } from "@/components/kanban/CaseDrawer";
import { CaseContextMenu } from "@/components/kanban/CaseContextMenu";
import { TaskFlame } from "@/components/tasks/TaskFlame";
import { Toast } from "@/components/ui/Toast";
import { useToast } from "@/lib/useToast";
import { useCaseTasks } from "@/lib/CaseTasksContext";
import { useCurrentProfile } from "@/lib/CurrentProfileContext";
import { sortColumnCases, tasksByCase } from "@/lib/kanbanTasks";
import { MessagesSlidePanel } from "@/components/kanban/MessagesSlidePanel";
import { SignaturesSlidePanel } from "@/components/kanban/SignaturesSlidePanel";
import { CreateCaseModal } from "@/components/kanban/CreateCaseModal";
import { SignatureCelebration } from "@/components/SignatureCelebration";
import { TodoTodayModal } from "@/components/kanban/TodoTodayModal";
import { BUDGET_BOUNDS, fullRange, isRangeActive, matchesDemandRanges, PAX_BOUNDS, type DemandRange } from "@/lib/kanbanFilters";
import { RangeFilter } from "@/components/kanban/RangeFilter";


export default function KanbanPage() {
  const { cases: loadedCases, loading, error, archivedLoaded, archivedLoading, loadArchivedCases } = useRealtimeCases();
  const { myOpenTasks } = useCaseTasks();
  const { isManager } = useCurrentProfile();
  const { toast, showToast, hideToast } = useToast();
  const [contextMenu, setContextMenu] = useState<{ caseId: string; x: number; y: number } | null>(null);

  // Priorité changée depuis le menu : affichée tout de suite, le temps que le temps réel confirme.
  const [priorityOverrides, setPriorityOverrides] = useState<Map<string, boolean>>(() => new Map());
  const cases = useMemo(
    () =>
      priorityOverrides.size === 0
        ? loadedCases
        : loadedCases.map((c) => (priorityOverrides.has(c.id) ? { ...c, is_priority: priorityOverrides.get(c.id)! } : c)),
    [loadedCases, priorityOverrides]
  );
  useEffect(() => {
    if (priorityOverrides.size === 0) return;
    const confirmed = loadedCases.filter((c) => priorityOverrides.get(c.id) === !!c.is_priority);
    if (confirmed.length === 0) return;
    setPriorityOverrides((prev) => {
      const next = new Map(prev);
      confirmed.forEach((c) => next.delete(c.id));
      return next;
    });
  }, [loadedCases, priorityOverrides]);
  const handlePriorityChange = useCallback((caseId: string, priority: boolean | null) => {
    setPriorityOverrides((prev) => {
      const next = new Map(prev);
      if (priority === null) next.delete(caseId);
      else next.set(caseId, priority);
      return next;
    });
  }, []);
  const closeContextMenu = useCallback(() => setContextMenu(null), []);
  const notify = useCallback(
    (message: string, type: "success" | "error") => showToast({ message, type, duration: type === "success" ? 3000 : 6000 }),
    [showToast]
  );

  // Mes tâches ouvertes par dossier (flamme) + compteur « À faire » qui s'anime quand il augmente.
  const myTasksByCase = useMemo(() => tasksByCase(myOpenTasks), [myOpenTasks]);
  const todoCount = myOpenTasks.length;
  const previousTodoCount = useRef(todoCount);
  const [todoBump, setTodoBump] = useState(0);
  useEffect(() => {
    if (todoCount > previousTodoCount.current) setTodoBump((n) => n + 1);
    previousTodoCount.current = todoCount;
  }, [todoCount]);
  const [showArchived, setShowArchived] = useState(false);
  const { notifications } = useNotifications();
  const { brand } = useBrand(); // Marque sélectionnée globalement
  const { selectedClientTypes, setAvailableClientTypes } = useClientTypeFilter();
  const [selectedCase, setSelectedCase] = useState<Case | null>(null);
  const [selectedCenter, setSelectedCenter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [demandFiltersOpen, setDemandFiltersOpen] = useState(false);
  const [todoOnly, setTodoOnly] = useState(false);
  const [paxRange, setPaxRange] = useState<DemandRange>(() => fullRange(PAX_BOUNDS));
  const [budgetRange, setBudgetRange] = useState<DemandRange>(() => fullRange(BUDGET_BOUNDS));
  // Le Kanban se refiltre en différé : les curseurs restent fluides pendant qu'on les fait glisser.
  const deferredPaxRange = useDeferredValue(paxRange);
  const deferredBudgetRange = useDeferredValue(budgetRange);
  const demandFilterCount = Number(isRangeActive(paxRange, PAX_BOUNDS)) + Number(isRangeActive(budgetRange, BUDGET_BOUNDS));
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [todoModalOpen, setTodoModalOpen] = useState(false);
  const [messagesSlideOpen, setMessagesSlideOpen] = useState(false);
  const [signaturesSlideOpen, setSignaturesSlideOpen] = useState(false);
  const [drawerInitialTab, setDrawerInitialTab] = useState<"summary" | "messages" | "quotes" | "invoices" | "logs">("summary");
  
  // État des colonnes collapsées (persisté dans localStorage)
  const [collapsedColumns, setCollapsedColumns] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem("kanban-collapsed-columns");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Réinitialiser le centre sélectionné quand on change de marque
  useEffect(() => {
    setSelectedCenter("all");
  }, [brand]);

  // Extraire les centres uniques (filtrés par marque)
  const centers = useMemo(() => {
    const brandFilteredCases = cases.filter(c => {
      const caseBrand = c.brand || detectBrandFromCenter(c.center);
      return caseBrand === brand;
    });
    const uniqueCenters = Array.from(new Set(brandFilteredCases.map(c => c.center).filter(Boolean))) as string[];
    if (brand === "smile") {
      return mergeCenterOptions(getCentersForBrand(brand), uniqueCenters);
    }
    return uniqueCenters.sort();
  }, [cases, brand]);

  // Extraire les client_type disponibles pour le dropdown header
  const availableClientTypes = CLIENT_TYPE_OPTIONS;

  useEffect(() => {
    setAvailableClientTypes([...availableClientTypes]);
  }, [availableClientTypes, setAvailableClientTypes]);

  // Filtrer les dossiers par marque, centre et recherche
  const filteredCases = useMemo(() => {
    return cases.filter(c => {
      // Filtre par marque (utilise le champ brand ou détecte via le centre)
      const caseBrand = c.brand || detectBrandFromCenter(c.center);
      const matchesBrand = caseBrand === brand;
      
      // Filtre par centre
      const matchesCenter = selectedCenter === "all" || c.center === selectedCenter;
      
      // Filtre par recherche (company_name)
      const matchesSearch = !searchQuery || 
        c.company_name?.toLowerCase().includes(searchQuery.toLowerCase());

      // Filtre par type de client (null => tous)
      const normalizedClientType = normalizeClientType(c.client_type);
      const matchesClientType = !selectedClientTypes
        ? true
        : !!normalizedClientType && selectedClientTypes.includes(normalizedClientType);
      
      // « À faire uniquement » : dossiers où j'ai une tâche ouverte
      const matchesTodo = !todoOnly || myTasksByCase.has(c.id);

      return matchesBrand && matchesCenter && matchesSearch && matchesClientType && matchesTodo && matchesDemandRanges(c, deferredPaxRange, deferredBudgetRange);
    });
  }, [cases, brand, selectedCenter, searchQuery, selectedClientTypes, todoOnly, myTasksByCase, deferredPaxRange, deferredBudgetRange]);

  // Groupes sans filtre de centre → pour les pastilles sur les CaseCards du kanban
  const { messageGroups: allMessageGroups, signatureItems: allSignatureItems } = useMemo(
    () => groupNotifications(notifications, []),
    [notifications]
  );

  const unreadCaseIds = useMemo(
    () => new Set(allMessageGroups.filter((item) => item.unreadCount > 0).map((item) => item.caseId)),
    [allMessageGroups]
  );

  const signatureCaseIds = useMemo(
    () => new Set(allSignatureItems.map((item) => item.caseId)),
    [allSignatureItems]
  );

  // Grouper les dossiers filtrés par statut
  // Relances devis : seulement les cartes où la barre s'affiche
  const reminderCaseIds = useMemo(
    () => filteredCases.filter((c) => c.status === "Devis envoyé" || c.status === "Signé").map((c) => c.id),
    [filteredCases]
  );
  const reminderCycles = useReminderCycles(reminderCaseIds);

  const casesByStatus = useMemo(() => {
    return orderedStatuses.reduce((acc, status) => {
      // Prioritaires (manager) en haut de chaque colonne ; « Non traité » garde ensuite son tri historique
      // (≥ 10 000 € HT d'abord, puis plus anciens).
      acc[status] = sortColumnCases(filteredCases.filter((c) => c.status === status), status);
      return acc;
    }, {} as Record<string, Case[]>);
  }, [filteredCases]);

  // Sauvegarder l'état dans localStorage
  useEffect(() => {
    localStorage.setItem("kanban-collapsed-columns", JSON.stringify(collapsedColumns));
  }, [collapsedColumns]);

  const toggleColumn = (status: string) => {
    setCollapsedColumns(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status);
      } else {
        return [...prev, status];
      }
    });
  };

  // Centres sélectionnés (contexte partagé, plus de polling)
  const { selectedCenters } = useSelectedCenters();

  // Groupes filtrés par centres sélectionnés → pour les badges des boutons
  const { messageGroups: filteredMessageGroups, signatureItems: filteredSignatureItems } = useMemo(
    () => groupNotifications(notifications, selectedCenters),
    [notifications, selectedCenters]
  );


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        {/* Skeleton Header */}
        <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="h-7 w-48 bg-gray-200 rounded animate-pulse" />
                <div className="h-4 w-32 bg-gray-100 rounded animate-pulse mt-2" />
              </div>
              <div className="flex items-center gap-3">
                <div className="h-9 w-28 bg-gray-200 rounded-lg animate-pulse" />
                <div className="h-9 w-28 bg-gray-200 rounded-lg animate-pulse" />
                <div className="w-px h-8 bg-gray-200" />
                <div className="h-9 w-24 bg-gray-200 rounded-lg animate-pulse" />
              </div>
            </div>
            <div className="flex gap-2 pb-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-9 w-32 bg-gray-200 rounded-lg animate-pulse" />
              ))}
            </div>
          </div>
        </div>
        {/* Skeleton Columns */}
        <div className="p-6 overflow-x-auto">
          <div className="flex gap-4 min-w-max">
            {Array.from({ length: 7 }).map((_, colIdx) => (
              <div key={colIdx} className="flex-shrink-0 w-80">
                <div className="rounded-t-lg p-3 bg-gray-200 animate-pulse">
                  <div className="h-4 w-24 bg-gray-300 rounded" />
                  <div className="h-3 w-16 bg-gray-300 rounded mt-1.5" />
                </div>
                <div className="bg-gray-50 rounded-b-lg p-3 min-h-[200px] border border-t-0 border-gray-200 space-y-3">
                  {Array.from({ length: 3 + (colIdx % 3) }).map((_, cardIdx) => (
                    <div key={cardIdx} className="bg-white rounded-lg border border-gray-200 p-3 animate-pulse">
                      <div className="h-4 w-3/4 bg-gray-200 rounded mb-2" />
                      <div className="h-3 w-1/2 bg-gray-100 rounded mb-3" />
                      <div className="flex gap-2 mb-2">
                        <div className="h-5 w-16 bg-gray-100 rounded" />
                        <div className="h-5 w-20 bg-gray-100 rounded" />
                      </div>
                      <div className="h-3 w-2/3 bg-gray-100 rounded" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-600">Erreur : {error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Kanban Commercial
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {filteredCases.length} dossier{filteredCases.length > 1 ? "s" : ""} 
                {searchQuery && " (filtrés)"}
                {selectedCenter !== "all" && ` - ${selectedCenter}`}
              </p>
            </div>

            {/* Actions à droite */}
            <div className="flex flex-wrap items-center gap-3">
              <button type="button" role="switch" aria-checked={todoOnly} onClick={() => setTodoOnly(v => !v)}
                title="N'afficher que les dossiers où j'ai une tâche à faire"
                className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium ${todoOnly ? "border-orange-200 bg-orange-50 text-orange-800" : "border-gray-300 bg-white text-gray-700"}`}>
                <span aria-hidden="true" className={`relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors ${todoOnly ? "bg-orange-500" : "bg-gray-300"}`}>
                  <span className={`absolute h-3 w-3 rounded-full bg-white shadow transition-transform ${todoOnly ? "translate-x-3.5" : "translate-x-0.5"}`} />
                </span>
                À faire uniquement
              </button>
              <button type="button" onClick={() => setDemandFiltersOpen(v => !v)} aria-expanded={demandFiltersOpen} aria-controls="kanban-demand-filters"
                className={`rounded-lg border px-3 py-2 text-sm font-medium ${demandFilterCount ? "border-indigo-200 bg-indigo-50 text-indigo-800" : "border-gray-300 bg-white text-gray-700"}`}>
                Filtres{demandFilterCount > 0 ? ` (${demandFilterCount})` : ""}
              </button>
              {/* Bouton Nouveaux messages */}
              <button
                onClick={() => { setMessagesSlideOpen(true); setSignaturesSlideOpen(false); }}
                className={`relative px-4 py-2 rounded-lg font-semibold shadow-sm hover:shadow-md transition-all flex items-center gap-2 text-sm ${
                  filteredMessageGroups.length > 0
                    ? "bg-gradient-to-r from-red-500 to-orange-500 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
                title="Voir les messages en attente"
              >
                <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <span className="hidden sm:inline">Messages</span>
                {filteredMessageGroups.length > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full text-xs font-bold bg-white/25 text-white">
                    {filteredMessageGroups.length}
                  </span>
                )}
              </button>

              {/* Bouton Nouvelles signatures */}
              <button
                onClick={() => { setSignaturesSlideOpen(true); setMessagesSlideOpen(false); }}
                className={`relative px-4 py-2 rounded-lg font-semibold shadow-sm hover:shadow-md transition-all flex items-center gap-2 text-sm ${
                  filteredSignatureItems.length > 0
                    ? "bg-gradient-to-r from-emerald-500 to-green-500 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
                title="Voir les signatures en attente"
              >
                <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="hidden sm:inline">Signatures</span>
                {filteredSignatureItems.length > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full text-xs font-bold bg-white/25 text-white">
                    {filteredSignatureItems.length}
                  </span>
                )}
              </button>

              {/* Séparateur visuel */}
              <div className="w-px h-8 bg-gray-200" />

              {/* Bouton À faire : mes tâches ouvertes (temps réel) */}
              <button
                type="button"
                onClick={() => setTodoModalOpen(true)}
                className={`pl-1.5 pr-3 py-1.5 rounded-lg font-semibold transition-all flex items-center gap-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-1 ${
                  todoCount > 0
                    ? "bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-md hover:shadow-lg"
                    : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                }`}
                title={todoCount > 0 ? `${todoCount} tâche${todoCount > 1 ? "s" : ""} à faire` : "Aucune tâche à faire"}
                aria-label={`À faire : ${todoCount} tâche${todoCount > 1 ? "s" : ""} ouverte${todoCount > 1 ? "s" : ""}`}
              >
                <span className={`flex h-6 w-6 items-center justify-center rounded-md ${todoCount > 0 ? "bg-white/95 shadow-sm" : "bg-orange-50"}`}>
                  <TaskFlame className="h-4 w-4" animated={todoCount > 0} />
                </span>
                <span>À faire</span>
                <span
                  key={todoBump}
                  className={`min-w-[1.375rem] px-1.5 py-0.5 rounded-full text-xs font-bold tabular-nums text-center ${todoBump > 0 ? "task-count-bump" : ""} ${
                    todoCount > 0 ? "bg-white text-red-600" : "bg-gray-100 text-gray-500"
                  }`}
                  aria-live="polite"
                >
                  {todoCount}
                </span>
              </button>

              {/* Bouton Nouveau dossier */}
              <button
                onClick={() => setCreateModalOpen(true)}
                className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                title="Ajouter un dossier"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>

              {/* Barre de recherche */}
              <div className="relative w-72">
                <input
                  type="text"
                  placeholder="Rechercher une entreprise..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                />
                <svg 
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          </div>

          {demandFiltersOpen && <div id="kanban-demand-filters" className="mb-4 flex flex-wrap items-start gap-8 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <RangeFilter id="kanban-pax" label="Nombre de participants" bounds={PAX_BOUNDS} unit="pax" value={paxRange} onChange={setPaxRange} unknownLabel="Effectif non renseigné" />
            <RangeFilter id="kanban-budget" label="Budget total de la demande" bounds={BUDGET_BOUNDS} unit="euros" value={budgetRange} onChange={setBudgetRange} unknownLabel="Budget non renseigné" />
            {demandFilterCount > 0 && <button type="button" onClick={() => { setPaxRange(fullRange(PAX_BOUNDS)); setBudgetRange(fullRange(BUDGET_BOUNDS)); }} className="self-center rounded-lg px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50">Réinitialiser les filtres</button>}
          </div>}
          {/* Barre : archivés + tabs centre */}
          <div className="flex items-center gap-3 pb-2">
            {/* Toggle Supprimé / Rejeté */}
            <button
              onClick={async () => {
                if (!archivedLoaded && !showArchived) {
                  await loadArchivedCases();
                }
                setShowArchived((v) => !v);
              }}
              disabled={archivedLoading}
              className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                showArchived
                  ? "bg-gray-800 text-white border-gray-800"
                  : "bg-white text-gray-500 border-gray-300 hover:bg-gray-50"
              }`}
              title={showArchived ? "Masquer les dossiers Supprimé / Rejeté" : "Afficher les dossiers Supprimé / Rejeté"}
            >
              {archivedLoading ? (
                <span className="flex items-center gap-1.5">
                  <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Chargement…
                </span>
              ) : showArchived ? (
                "✕ Masquer archivés"
              ) : (
                "📦 Supprimé / Rejeté"
              )}
            </button>

            <div className="w-px h-6 bg-gray-200 flex-shrink-0" />

          {/* Tabs par centre */}
          <div className="flex gap-2 overflow-x-auto">
            <button
              onClick={() => setSelectedCenter("all")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                selectedCenter === "all"
                  ? "bg-indigo-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Tous les centres ({cases.filter(c => (c.brand || detectBrandFromCenter(c.center)) === brand).length})
            </button>
            {centers.map((center) => {
              const centerCount = cases.filter(c => c.center === center && (c.brand || detectBrandFromCenter(c.center)) === brand).length;
              return (
                <button
                  key={center}
                  onClick={() => setSelectedCenter(center)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    selectedCenter === center
                      ? "bg-indigo-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {center} ({centerCount})
                </button>
              );
            })}
          </div>
        </div>
        </div>
      </header>

      {/* Kanban Board */}
      <div className="p-6 overflow-x-auto">
        <div className="flex gap-4 min-w-max">
          {orderedStatuses
            .filter((status) => showArchived || (status !== "Supprimé" && status !== "Rejeté"))
            .map((status) => {
            const statusCases = casesByStatus[status] || [];
            const isCollapsed = collapsedColumns.includes(status);
            
            return (
              <div
                key={status}
                className={`flex-shrink-0 transition-all ${isCollapsed ? "w-16" : "w-80"}`}
              >
                {/* Column Header */}
                <div
                  className="rounded-t-lg p-3 border-b-2 flex items-center justify-between cursor-pointer"
                  style={{
                    backgroundColor: statusColors[status],
                    borderBottomColor: statusColors[status],
                  }}
                  onClick={() => toggleColumn(status)}
                >
                  <div className={isCollapsed ? "hidden" : ""}>
                    <h2 className="font-semibold text-sm text-gray-900">
                      {status}
                    </h2>
                    <p className="text-xs text-gray-600 mt-0.5">
                      {statusCases.length} dossier{statusCases.length > 1 ? "s" : ""}
                    </p>
                  </div>
                  
                  {isCollapsed && (
                    <div className="transform -rotate-90 whitespace-nowrap text-xs font-semibold text-gray-900">
                      {status} ({statusCases.length})
                    </div>
                  )}
                  
                  <button
                    className="ml-2 text-gray-700 hover:text-gray-900 font-bold text-lg"
                    title={isCollapsed ? "Agrandir" : "Réduire"}
                  >
                    {isCollapsed ? "+" : "−"}
                  </button>
                </div>

                {/* Column Content */}
                {!isCollapsed && (
                  <div
                    className="bg-gray-50 rounded-b-lg p-3 min-h-[200px] max-h-[calc(100vh-320px)] overflow-y-auto space-y-3 border border-t-0 border-gray-200"
                  >
                    {statusCases.length === 0 ? (
                      <div className="text-center text-gray-400 text-sm py-8">
                        Aucun dossier
                      </div>
                    ) : (
                      statusCases.map((caseItem) => (
                        <CaseCard
                          key={caseItem.id}
                          case={caseItem}
                          onClick={() => setSelectedCase(caseItem)}
                          hasUnread={unreadCaseIds.has(caseItem.id)}
                          hasRecentSignature={signatureCaseIds.has(caseItem.id)}
                          reminderCycle={reminderCycles.get(caseItem.id)}
                          myTasks={myTasksByCase.get(caseItem.id)}
                          onContextMenu={
                            isManager
                              ? (event) => {
                                  event.preventDefault();
                                  setContextMenu({ caseId: caseItem.id, x: event.clientX, y: event.clientY });
                                }
                              : undefined
                          }
                        />
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Drawer */}
      {selectedCase && (
        <CaseDrawer
          case={selectedCase}
          onClose={() => { setSelectedCase(null); setDrawerInitialTab("summary"); }}
          initialTab={drawerInitialTab}
        />
      )}

      {/* Modal Création de dossier */}
      <CreateCaseModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={(caseId) => {
          // Optionnel : ouvrir automatiquement le drawer du nouveau case
          const newCase = cases.find(c => c.id === caseId);
          if (newCase) {
            setSelectedCase(newCase);
          }
        }}
      />

      {/* Modal À faire aujourd'hui */}
      <TodoTodayModal
        isOpen={todoModalOpen}
        onClose={() => setTodoModalOpen(false)}
        cases={cases}
        onCaseClick={(caseData) => {
          setSelectedCase(caseData);
          setTodoModalOpen(false);
        }}
      />

      {/* Slide panel : Nouveaux messages */}
      <MessagesSlidePanel
        isOpen={messagesSlideOpen}
        onClose={() => setMessagesSlideOpen(false)}
        onCaseClick={(caseId) => {
          const caseToOpen = cases.find((c) => c.id === caseId);
          if (caseToOpen) {
            setDrawerInitialTab("messages");
            setSelectedCase(caseToOpen);
          }
        }}
        cases={cases}
      />

      {/* Slide panel : Nouvelles signatures */}
      <SignaturesSlidePanel
        isOpen={signaturesSlideOpen}
        onClose={() => setSignaturesSlideOpen(false)}
        onCaseClick={(caseId) => {
          const caseToOpen = cases.find((c) => c.id === caseId);
          if (caseToOpen) {
            setDrawerInitialTab("quotes");
            setSelectedCase(caseToOpen);
          }
        }}
        cases={cases}
      />

      {/* Menu clic droit (manager) */}
      {contextMenu && (() => {
        const target = cases.find((c) => c.id === contextMenu.caseId);
        return target ? (
          <CaseContextMenu
            key={`${contextMenu.caseId}-${contextMenu.x}-${contextMenu.y}`}
            caseData={target}
            x={contextMenu.x}
            y={contextMenu.y}
            onClose={closeContextMenu}
            onNotify={notify}
            onPriorityChange={handlePriorityChange}
          />
        ) : null;
      })()}

      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} duration={toast.duration} />

      {/* Easter egg: Animation de signature 💰 */}
      <SignatureCelebration />
    </div>
  );
}
